import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { Router } from '@angular/router';
import { AppStore } from '@app/shared/stores/app.store';
import { BrandEnum } from '@infrastructure/brand.enum';
import { SubscribingComponent } from '@infrastructure/subscribing-component';
import { AbortAftersalesComponent } from '@modals/abort-aftersales.component';
import { CustomerType } from '@models/customer';
import { SalesDistrictType } from "@models/sales-district";
import { TranslatePipe } from '@ngx-translate/core';
import { BrandModeService } from '@services/brandmode.service';
import { EventsService } from "@services/events.service";
import { GoogleAnalyticsService } from "@services/google-analytics.service";
import { ModalService } from "@services/modal.service";
import { OrderOnBehalfService } from '@services/order-on-behalf.service';
import { ThemeService } from '@services/theme.service';
import { UserService } from "@services/user.service";
import { AppModeEnum, AppState } from "@shared/app-state";
import { FadeInOutTransition } from '@shared/transitions/fade-in-out.transition';

@Component({
	selector: 'on-behalf-of-bar',
	templateUrl: 'selected-customer-bar.component.html',
	styleUrls: ["./selected-customer-bar.component.scss"],
	animations: [
		FadeInOutTransition()
	],
	// eslint-disable-next-line @angular-eslint/no-host-metadata-property
	host: {
		"[@fadeInOutTransition]": ''
	},
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [CommonModule, TranslatePipe]
})
export class SelectedCustomerBarComponent extends SubscribingComponent {

	router = inject(Router)
	appStore = inject(AppStore)
	themeService = inject(ThemeService);

	brandModeService = inject(BrandModeService);

	public customerType: string | null;
	public customerName: string | null;
	doShow: boolean;

	appModeEnum = AppModeEnum;
	public addToBagInProgress: boolean = false;

	constructor(
		private onBehalfOfService: OrderOnBehalfService,
		private googleAnalyticsService: GoogleAnalyticsService,
		private userService: UserService,
		private modalService: ModalService,
		private eventsService: EventsService,
		public appState: AppState
	) {
		super();

		appState.appMode.subscribe(am => this.doShow = ((am === AppModeEnum.repair) || (am === AppModeEnum.substitution)));

		this.subscriptions.push(onBehalfOfService.customerAsObservable().subscribe(async x => {
			if (x) {
				this.customerType = x ? x.Type === CustomerType.OrderOnBehalf ? 'CUSTOMERS.CHANGE-CUSTOMER' : 'CUSTOMERS.CHANGE-SHOP' : null;
				this.customerName = x ? `${x.Name}${x.Street ? ', ' + x.Street : ''}` : null;

				if (!x.IsNewPartner || x.SalesDistrictType === SalesDistrictType.OwnDistrict) {
					this.customerName += ` (${x.No})`;
				}

				const user = await this.userService.GetUser();

				if (user) {
					await this.googleAnalyticsService.eventEmitter('partner_change', 'engagement', `${user.Uuid} changed partner to ${x.No} (${x.Name})`, 10);
				}
			}
		}));

		this.appState.hasActiveAddToBagExecutionsStatus.subscribe(status => {
			this.addToBagInProgress = status;
		});
	}

	private activateTitanium() {
		if (this.brandModeService.currentBrandValue() !== BrandEnum.Titanium) {
			this.brandModeService.toggleBrandMode(BrandEnum.Titanium);
		}

		if (this.appState.hasActiveAddToBagExecutionsStatus.value) {
			return;
		}

		if (this.appStore.ref()) {
			this.themeService.updateTheme('titanium', this.appStore.ref());
		}
	}

	goToCustomerSelector() {
		if (this.addToBagInProgress) {
			return;
		}

		this.activateTitanium();

		this.router.navigate(['/customer']);
	}

	async abortAfterSales() {
		this.modalService.create(AbortAftersalesComponent, { content: "" });
		this.eventsService.showLoader(false);
	}
}
